/**
 * The renderer.
 *
 * Canvas 2D, but treated like a light rig rather than a tile grid. Everything
 * emissive is drawn twice — once sharp on the main canvas, once into a
 * half-resolution buffer that gets blurred and added back on top. That second
 * pass is the whole look: real bloom, so the snake actually appears to light
 * the lattice it's crawling over.
 *
 * Motion is interpolated between simulation ticks, so the sim can run at a
 * chunky 8-14Hz while the snake glides at whatever the display can do.
 */

import { bloomCycle, bloomOpen } from '../core/sim'
import type { Rules, SimState, Vec } from '../core/types'
import { DIR_VECTORS } from '../core/types'
import { Particles } from './particles'
import { COLORS, clamp01, heatColor, lerp, paletteFor, rgba } from './palette'

export interface RenderInput {
  sim: SimState
  prev: Vec[]
  alpha: number
  rules: Rules
  /** Seconds since the last frame. Everything time-based scales off this so
   *  effects last the same wall-clock time at 30, 60 or 144Hz. */
  dt: number
  time: number
  paused: boolean
  reducedMotion: boolean
  showLattice: boolean
  showScanlines: boolean
}

interface Dust {
  x: number
  y: number
  z: number
  s: number
}

interface Shock {
  x: number
  y: number
  t: number
  life: number
  color: [number, number, number]
}

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
]

const C_MOTE = hexToRgb(COLORS.mote)
const C_EMBER = hexToRgb(COLORS.ember)
const C_PRISM = hexToRgb(COLORS.prism)
const C_DANGER = hexToRgb(COLORS.danger)
const C_BLOOM = hexToRgb(COLORS.bloomOpen)

export class Renderer {
  readonly particles = new Particles()

  private ctx: CanvasRenderingContext2D
  private glow: HTMLCanvasElement
  private gctx: CanvasRenderingContext2D
  private supportsFilter: boolean

  private w = 0
  private h = 0
  private dpr = 1
  private cell = 20
  private originX = 0
  private originY = 0

  private shakeAmt = 0
  private flashAmt = 0
  private flashColor: [number, number, number] = [255, 255, 255]
  private dust: Dust[] = []
  private shocks: Shock[] = []
  private headLight = 0
  private deathAt = 0

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('canvas 2d unavailable')
    this.ctx = ctx
    this.glow = document.createElement('canvas')
    const gctx = this.glow.getContext('2d')
    if (!gctx) throw new Error('canvas 2d unavailable')
    this.gctx = gctx
    this.supportsFilter = typeof ctx.filter === 'string'
    for (let i = 0; i < 90; i++) {
      this.dust.push({ x: Math.random(), y: Math.random(), z: 0.2 + Math.random() * 0.8, s: Math.random() })
    }
  }

  resize(grid: number): void {
    const rect = this.canvas.getBoundingClientRect()
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const w = Math.max(200, Math.round(rect.width))
    const h = Math.max(200, Math.round(rect.height))
    if (w === this.w && h === this.h && dpr === this.dpr) {
      this.layout(grid)
      return
    }
    this.w = w
    this.h = h
    this.dpr = dpr
    this.canvas.width = Math.round(w * dpr)
    this.canvas.height = Math.round(h * dpr)
    this.glow.width = Math.max(1, Math.round((w * dpr) / 2))
    this.glow.height = Math.max(1, Math.round((h * dpr) / 2))
    this.layout(grid)
  }

  private layout(grid: number): void {
    const side = Math.min(this.w, this.h)
    this.cell = side / grid
    this.originX = (this.w - this.cell * grid) / 2
    this.originY = (this.h - this.cell * grid) / 2
  }

  shake(amount: number): void {
    this.shakeAmt = Math.min(28, this.shakeAmt + amount)
  }

  flash(color: [number, number, number], amount: number): void {
    this.flashColor = color
    this.flashAmt = Math.min(1, this.flashAmt + amount)
  }

  shock(x: number, y: number, color: [number, number, number], life = 0.8): void {
    this.shocks.push({ x, y, t: 0, life, color })
  }

  reset(): void {
    this.particles.clear()
    this.shocks.length = 0
    this.shakeAmt = 0
    this.flashAmt = 0
    this.deathAt = 0
  }

  markDeath(time: number): void {
    this.deathAt = time
  }

  // ── frame ──────────────────────────────────────────────────────────────

  render(input: RenderInput): void {
    const { sim, time, reducedMotion } = input
    const ctx = this.ctx
    const g = this.gctx
    const pal = paletteFor(sim.coil)

    this.resize(sim.grid)
    const dt = Math.min(0.1, input.dt)
    const decay = dt * 60

    if (this.shakeAmt > 0) this.shakeAmt *= Math.pow(0.86, decay)
    if (this.flashAmt > 0) this.flashAmt *= Math.pow(0.9, decay)
    for (let i = this.shocks.length - 1; i >= 0; i--) {
      this.shocks[i].t += dt
      if (this.shocks[i].t > this.shocks[i].life) this.shocks.splice(i, 1)
    }

    ctx.save()
    ctx.scale(this.dpr, this.dpr)
    g.save()
    g.scale(this.dpr / 2, this.dpr / 2)
    g.clearRect(0, 0, this.w, this.h)

    // Screen shake is a camera move, so it has to happen before anything is
    // placed — including the glow buffer, or bloom would drift off its source.
    const sh = reducedMotion ? 0 : this.shakeAmt
    const ox = sh ? (Math.random() - 0.5) * sh : 0
    const oy = sh ? (Math.random() - 0.5) * sh : 0

    this.drawVoid(ctx, pal, time, reducedMotion)

    // Board space. Both contexts get their own save so the composite and the
    // overlays below stay in screen space *and* keep the dpr scale.
    ctx.save()
    g.save()
    ctx.translate(this.originX + ox, this.originY + oy)
    g.translate(this.originX + ox, this.originY + oy)

    const head = this.interpolatedHead(input)
    this.headLight = lerp(this.headLight, sim.alive ? 1 : 0.15, Math.min(1, 0.08 * decay))

    if (input.showLattice) this.drawLattice(ctx, pal, sim, head, time)
    this.drawArena(ctx, g, pal, sim, time)

    this.drawBlooms(ctx, g, sim, time)
    this.drawPickups(ctx, g, sim, time)
    this.drawSnake(ctx, g, input)
    this.drawShocks(g)
    this.particles.update(dt)
    this.particles.draw(g, this.cell)

    ctx.restore() // leave board space, still dpr-scaled
    g.restore()
    g.restore() // glow buffer is finished with

    // ── bloom composite ──────────────────────────────────────────────────
    // The context is still scaled by dpr here, so the destination rect is in
    // CSS pixels — the half-resolution buffer is upscaled back to full size,
    // which is exactly the cheap softening we want before the blur.
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    if (this.supportsFilter) {
      ctx.filter = `blur(${Math.max(2, this.w * 0.006)}px)`
      ctx.globalAlpha = 0.62
      ctx.drawImage(this.glow, 0, 0, this.w, this.h)
      ctx.filter = `blur(${Math.max(6, this.w * 0.022)}px)`
      ctx.globalAlpha = 0.48
      ctx.drawImage(this.glow, 0, 0, this.w, this.h)
      ctx.filter = 'none'
    } else {
      ctx.globalAlpha = 0.5
      ctx.drawImage(this.glow, 0, 0, this.w, this.h)
    }
    ctx.restore()

    this.drawOverlays(ctx, input)
    ctx.restore()
  }

  // ── layers ─────────────────────────────────────────────────────────────

  private drawVoid(ctx: CanvasRenderingContext2D, pal: ReturnType<typeof paletteFor>, time: number, reduced: boolean): void {
    const grad = ctx.createLinearGradient(0, 0, 0, this.h)
    grad.addColorStop(0, pal.voidTop)
    grad.addColorStop(1, pal.voidBottom)
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, this.w, this.h)

    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    const drift = reduced ? 0 : time
    for (const d of this.dust) {
      const x = ((d.x + drift * 0.004 * d.z) % 1) * this.w
      const y = ((d.y + drift * 0.011 * d.z) % 1) * this.h
      const a = 0.05 + d.z * 0.12 + Math.sin(time * 1.6 + d.s * 40) * 0.04
      ctx.fillStyle = `${pal.dust}${Math.round(clamp01(a) * 255)
        .toString(16)
        .padStart(2, '0')}`
      ctx.fillRect(x, y, d.z * 1.9, d.z * 1.9)
    }
    ctx.restore()
  }

  private drawLattice(
    ctx: CanvasRenderingContext2D,
    pal: ReturnType<typeof paletteFor>,
    sim: SimState,
    head: Vec,
    time: number,
  ): void {
    const n = sim.grid
    const c = this.cell
    ctx.save()
    ctx.strokeStyle = pal.lattice
    ctx.lineWidth = 1
    ctx.globalAlpha = 0.95
    ctx.beginPath()
    for (let i = 0; i <= n; i++) {
      ctx.moveTo(i * c, 0)
      ctx.lineTo(i * c, n * c)
      ctx.moveTo(0, i * c)
      ctx.lineTo(n * c, i * c)
    }
    ctx.stroke()

    // The snake is the light source: bloom the lattice around the head so the
    // grid appears lit rather than drawn.
    const r = c * (5.5 + Math.sin(time * 2.4) * 0.5)
    const lampGrad = ctx.createRadialGradient(head.x * c, head.y * c, 0, head.x * c, head.y * c, r)
    lampGrad.addColorStop(0, `${pal.latticeGlow}30`)
    lampGrad.addColorStop(0.5, `${pal.latticeGlow}12`)
    lampGrad.addColorStop(1, `${pal.latticeGlow}00`)
    ctx.globalCompositeOperation = 'lighter'
    ctx.globalAlpha = 0.9 * this.headLight
    ctx.fillStyle = lampGrad
    ctx.beginPath()
    ctx.arc(head.x * c, head.y * c, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  private drawArena(
    ctx: CanvasRenderingContext2D,
    g: CanvasRenderingContext2D,
    pal: ReturnType<typeof paletteFor>,
    sim: SimState,
    time: number,
  ): void {
    const side = sim.grid * this.cell
    const phased = sim.phase > 0
    const head = sim.snake[0]
    // Warn when the head is one cell from a wall — the single most common
    // death, and entirely avoidable if the player can see it coming.
    const near = Math.min(head.x, head.y, sim.grid - 1 - head.x, sim.grid - 1 - head.y)
    const danger = sim.alive && !phased && near <= 1 ? (near === 0 ? 1 : 0.45) : 0

    for (const target of [ctx, g]) {
      target.save()
      target.lineWidth = target === g ? 5 : 2.5
      if (phased) {
        const p = 0.5 + Math.sin(time * 9) * 0.3
        target.strokeStyle = rgba(C_PRISM, 0.5 + p * 0.5)
        target.setLineDash([this.cell * 0.5, this.cell * 0.35])
        target.lineDashOffset = -time * this.cell * 3
      } else if (danger > 0) {
        target.strokeStyle = rgba(C_DANGER, 0.35 + danger * 0.5 * (0.7 + Math.sin(time * 14) * 0.3))
      } else {
        target.strokeStyle = `${pal.latticeGlow}55`
      }
      target.strokeRect(-1.5, -1.5, side + 3, side + 3)
      target.restore()
    }
  }

  private drawBlooms(ctx: CanvasRenderingContext2D, g: CanvasRenderingContext2D, sim: SimState, time: number): void {
    const c = this.cell
    for (const b of sim.blooms) {
      const cyc = bloomCycle(b, sim.tick)
      const open = bloomOpen(b, sim.tick) ? 1 : 0
      const openRatio = b.openFor / b.period
      // Tell the player it's about to bite: wind up over the last stretch of
      // the closed phase instead of snapping open with no warning.
      const untilOpen = cyc >= openRatio ? (1 - cyc) / (1 - openRatio) : 0
      const wind = untilOpen > 0 ? clamp01(1 - untilOpen) : 0
      const t = open ? 1 : wind * 0.75
      const cx = (b.x + 0.5) * c
      const cy = (b.y + 0.5) * c
      const petals = 6
      // A dormant bloom still occupies a cell you have to plan around, so it
      // never shrinks to a speck — it closes into a visible bud.
      const R = c * (0.3 + t * 0.28)
      const spin = time * (open ? 1.1 : 0.35) + b.phase

      // Dormant ring: marks the danger cell even at the bottom of the cycle.
      ctx.save()
      ctx.strokeStyle = open ? rgba(C_BLOOM, 0.5) : `rgba(150,70,255,${0.22 + wind * 0.3})`
      ctx.lineWidth = 1.5
      ctx.setLineDash([c * 0.14, c * 0.1])
      ctx.lineDashOffset = -spin * c * 0.5
      ctx.beginPath()
      ctx.arc(cx, cy, c * 0.44, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()

      for (const target of [ctx, g]) {
        target.save()
        target.translate(cx, cy)
        target.rotate(spin)
        target.fillStyle = open
          ? rgba(C_BLOOM, 0.85)
          : `rgba(150,70,255,${0.3 + wind * 0.5})`
        target.beginPath()
        for (let i = 0; i < petals; i++) {
          const a = (i / petals) * Math.PI * 2
          const r2 = R * (i % 2 === 0 ? 1 : 0.55)
          target.lineTo(Math.cos(a) * r2, Math.sin(a) * r2)
          const a2 = ((i + 0.5) / petals) * Math.PI * 2
          target.lineTo(Math.cos(a2) * R * 0.3, Math.sin(a2) * R * 0.3)
        }
        target.closePath()
        target.fill()
        target.restore()
      }

      if (open) {
        ctx.save()
        ctx.strokeStyle = rgba(C_BLOOM, 0.25 + Math.sin(time * 8 + b.phase) * 0.12)
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(cx, cy, c * 0.46, 0, Math.PI * 2)
        ctx.stroke()
        ctx.restore()
        if (Math.random() < 0.25) {
          this.particles.trail(b.x + 0.5 + (Math.random() - 0.5) * 0.6, b.y + 0.5 + (Math.random() - 0.5) * 0.6, C_BLOOM)
        }
      }
    }
  }

  private drawPickups(ctx: CanvasRenderingContext2D, g: CanvasRenderingContext2D, sim: SimState, time: number): void {
    const c = this.cell
    for (const p of sim.pickups) {
      const cx = (p.x + 0.5) * c
      const cy = (p.y + 0.5) * c
      const age = sim.tick - p.born
      // Never fully invisible: a mote that isn't on screen for its first few
      // ticks reads as the game cheating you out of reaction time.
      const pop = 0.45 + 0.55 * clamp01(age / 5)

      if (p.kind === 'mote') {
        const pulse = 0.78 + Math.sin(time * 4.5) * 0.14
        const r = c * 0.24 * pulse * pop
        for (const target of [ctx, g]) {
          target.save()
          const grad = target.createRadialGradient(cx, cy, 0, cx, cy, r * 2.6)
          grad.addColorStop(0, COLORS.moteCore)
          grad.addColorStop(0.35, COLORS.mote)
          grad.addColorStop(1, rgba(C_MOTE, 0))
          target.fillStyle = grad
          target.beginPath()
          target.arc(cx, cy, r * 2.6, 0, Math.PI * 2)
          target.fill()
          target.restore()
        }
        // Three sparks in orbit — reads as "alive" from across the room.
        for (let i = 0; i < 3; i++) {
          const a = time * 2.2 + (i / 3) * Math.PI * 2
          const rr = c * 0.42
          g.fillStyle = rgba(C_MOTE, 0.8)
          g.beginPath()
          g.arc(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, c * 0.05, 0, Math.PI * 2)
          g.fill()
        }
      } else if (p.kind === 'ember') {
        const burn = clamp01(p.ttl / 60)
        const flick = 0.75 + Math.sin(time * 17 + p.x * 3 + p.y * 5) * 0.25
        const r = c * (0.13 + 0.12 * burn) * flick * pop
        for (const target of [ctx, g]) {
          target.save()
          target.translate(cx, cy)
          target.rotate(Math.PI / 4)
          target.fillStyle = COLORS.ember
          target.fillRect(-r, -r, r * 2, r * 2)
          target.fillStyle = COLORS.emberCore
          target.fillRect(-r * 0.4, -r * 0.4, r * 0.8, r * 0.8)
          target.restore()
        }
        if (Math.random() < 0.12) this.particles.trail(p.x + 0.5, p.y + 0.5, C_EMBER)
      } else {
        const spin = time * 1.6
        const r = c * 0.3 * pop * (0.85 + Math.sin(time * 6) * 0.15)
        const fading = p.ttl < 30 && Math.floor(time * 10) % 2 === 0
        for (const target of [ctx, g]) {
          target.save()
          target.translate(cx, cy)
          target.globalAlpha = fading ? 0.35 : 1
          for (let k = 0; k < 3; k++) {
            target.save()
            target.rotate(spin + (k / 3) * Math.PI * 2)
            target.fillStyle = k === 0 ? COLORS.prismCore : COLORS.prism
            target.globalAlpha = (fading ? 0.35 : 1) * (k === 0 ? 1 : 0.55)
            target.beginPath()
            target.moveTo(0, -r)
            target.lineTo(r * 0.86, r * 0.5)
            target.lineTo(-r * 0.86, r * 0.5)
            target.closePath()
            target.fill()
            target.restore()
          }
          target.restore()
        }
        if (Math.random() < 0.3) this.particles.trail(p.x + 0.5, p.y + 0.5, C_PRISM)
      }
    }
  }

  private drawSnake(ctx: CanvasRenderingContext2D, g: CanvasRenderingContext2D, input: RenderInput): void {
    const { sim, rules, time } = input
    const c = this.cell
    const lines = this.snakePolylines(input)
    const heat = rules.chainMax > 0 ? sim.chain / rules.chainMax : clamp01((sim.snake.length - rules.startLength) / 30)
    const base = heatColor(heat)
    // The tail is a cooler, deeper point on the same heat ramp — not the head
    // colour dimmed. Multiplying a white-hot head toward black just makes
    // grey; walking back down the ramp keeps it molten.
    const tail = heatColor(clamp01(heat - 0.38)).map((v) => v * 0.5) as [number, number, number]
    const phased = sim.phase > 0
    const dying = !sim.alive
    const lightRatio = rules.lightEnabled ? clamp01(sim.light / rules.lightMax) : 1
    // Running low doesn't just tick a bar down — the snake itself dims.
    const dim = rules.lightEnabled ? 0.35 + lightRatio * 0.65 : 1

    // Segments are stroked individually so the body can taper and pulse, which
    // means they overlap — so every stroke has to be fully opaque. Semi-
    // transparent strokes double-blend where they meet and the snake turns
    // into a string of beads. Brightness lives in the colour, never in alpha.
    for (const target of [ctx, g]) {
      const isGlow = target === g
      target.save()
      target.lineCap = 'round'
      target.lineJoin = 'round'
      if (phased) target.globalAlpha = 0.62 + Math.sin(time * 12) * 0.1

      for (const pts of lines) {
        for (let i = pts.length - 1; i > 0; i--) {
          const k = i / Math.max(1, pts.length - 1) // 0 at head, 1 at tail
          const taper = lerp(1, 0.58, k * k)
          // A slow pulse travels head-to-tail; the body looks like it's
          // conducting light rather than being painted.
          const wave = 0.88 + Math.sin(time * 6 - i * 0.45) * 0.12
          const fall = dim * wave * (isGlow ? 0.78 : 1)
          const a = pts[i]
          const b = pts[i - 1]

          const body: [number, number, number] = dying
            ? [C_DANGER[0] * (0.75 - k * 0.35), C_DANGER[1] * 0.2, C_DANGER[2] * 0.3]
            : [
                lerp(base[0], tail[0], k) * fall,
                lerp(base[1], tail[1], k) * fall,
                lerp(base[2], tail[2], k) * fall,
              ]

          target.strokeStyle = rgba(body, 1)
          target.lineWidth = c * 0.88 * taper
          target.beginPath()
          target.moveTo(a.x * c, a.y * c)
          target.lineTo(b.x * c, b.y * c)
          target.stroke()

          if (!dying) {
            // Keep the white core tight to the head. Spread it down the body
            // and the bloom pass turns the whole snake into bone.
            const core = 0.42 * Math.max(0, 1 - k * 2.2)
            target.strokeStyle = rgba(
              [lerp(body[0], 255, core), lerp(body[1], 255, core), lerp(body[2], 255, core)],
              1,
            )
            target.lineWidth = c * 0.3 * taper
            target.beginPath()
            target.moveTo(a.x * c, a.y * c)
            target.lineTo(b.x * c, b.y * c)
            target.stroke()
          }
        }
      }
      target.restore()
    }

    // ── head ─────────────────────────────────────────────────────────────
    const headPt = lines[0]?.[0] ?? { x: sim.snake[0].x + 0.5, y: sim.snake[0].y + 0.5 }
    const dv = DIR_VECTORS[sim.dir]
    const hr = c * 0.48
    for (const target of [ctx, g]) {
      target.save()
      target.translate(headPt.x * c, headPt.y * c)
      target.rotate(Math.atan2(dv.y, dv.x))
      target.fillStyle = dying ? rgba(C_DANGER, 0.85) : rgba(base, dim)
      target.beginPath()
      target.ellipse(0, 0, hr * 1.08, hr * 0.94, 0, 0, Math.PI * 2)
      target.fill()
      if (!dying) {
        target.fillStyle = rgba([255, 255, 255], 0.9 * dim)
        target.beginPath()
        target.ellipse(hr * 0.18, 0, hr * 0.42, hr * 0.36, 0, 0, Math.PI * 2)
        target.fill()
      }
      target.restore()
    }

    // Eyes track forward; the pupils pinch shut as the light runs out.
    if (!dying) {
      ctx.save()
      ctx.translate(headPt.x * c, headPt.y * c)
      ctx.rotate(Math.atan2(dv.y, dv.x))
      const squint = rules.lightEnabled ? lerp(0.3, 1, lightRatio) : 1
      for (const side of [-1, 1]) {
        ctx.fillStyle = 'rgba(8,10,20,0.92)'
        ctx.beginPath()
        ctx.ellipse(hr * 0.34, side * hr * 0.34, hr * 0.17, hr * 0.19 * squint, 0, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.restore()
    }

    // Fangs ready? A tiny mark under the head, so the bite window is legible
    // in the corner of the eye without ever looking at the HUD.
    if (rules.biteEnabled && sim.alive) {
      const ready = sim.biteCooldown === 0 && sim.snake.length >= rules.biteMinLength
      g.save()
      g.globalAlpha = ready ? 0.9 : 0.25
      g.fillStyle = ready ? COLORS.ember : '#556'
      g.beginPath()
      g.arc(headPt.x * c, headPt.y * c, c * (ready ? 0.62 : 0.55), 0, Math.PI * 2)
      g.globalAlpha *= 0.25
      g.fill()
      g.restore()
    }

    if (!input.reducedMotion && sim.alive && Math.random() < 0.5) {
      this.particles.trail(headPt.x, headPt.y, base)
    }
  }

  private drawShocks(g: CanvasRenderingContext2D): void {
    const c = this.cell
    for (const s of this.shocks) {
      const t = s.t / s.life
      g.save()
      g.globalCompositeOperation = 'lighter'
      g.strokeStyle = rgba(s.color, (1 - t) * 0.8)
      g.lineWidth = c * 0.5 * (1 - t)
      g.beginPath()
      g.arc((s.x + 0.5) * c, (s.y + 0.5) * c, c * (0.5 + t * 14), 0, Math.PI * 2)
      g.stroke()
      g.restore()
    }
  }

  private drawOverlays(ctx: CanvasRenderingContext2D, input: RenderInput): void {
    const { sim, rules, time } = input

    // vignette
    const vg = ctx.createRadialGradient(
      this.w / 2,
      this.h / 2,
      Math.min(this.w, this.h) * 0.32,
      this.w / 2,
      this.h / 2,
      Math.max(this.w, this.h) * 0.78,
    )
    vg.addColorStop(0, 'rgba(0,0,0,0)')
    vg.addColorStop(1, 'rgba(0,0,0,0.72)')
    ctx.fillStyle = vg
    ctx.fillRect(0, 0, this.w, this.h)

    // Low light closes in from the edges. You feel it before you read it.
    if (rules.lightEnabled && sim.alive) {
      const lr = clamp01(sim.light / rules.lightMax)
      if (lr < 0.3) {
        const urgency = (0.3 - lr) / 0.3
        const pulse = 0.5 + Math.sin(time * (4 + urgency * 6)) * 0.5
        const dg = ctx.createRadialGradient(
          this.w / 2,
          this.h / 2,
          Math.min(this.w, this.h) * (0.42 - urgency * 0.2),
          this.w / 2,
          this.h / 2,
          Math.max(this.w, this.h) * 0.7,
        )
        dg.addColorStop(0, 'rgba(0,0,0,0)')
        dg.addColorStop(1, `rgba(${input.reducedMotion ? '10,10,14' : '40,8,14'},${0.3 + urgency * pulse * 0.55})`)
        ctx.fillStyle = dg
        ctx.fillRect(0, 0, this.w, this.h)
      }
    }

    if (sim.phase > 0) {
      ctx.save()
      ctx.globalCompositeOperation = 'lighter'
      ctx.fillStyle = rgba(C_PRISM, 0.05 + Math.sin(time * 10) * 0.02)
      ctx.fillRect(0, 0, this.w, this.h)
      ctx.restore()
    }

    if (this.flashAmt > 0.01) {
      ctx.save()
      ctx.globalCompositeOperation = 'lighter'
      ctx.fillStyle = rgba(this.flashColor, this.flashAmt * 0.5)
      ctx.fillRect(0, 0, this.w, this.h)
      ctx.restore()
    }

    // Death drains the colour out of the arena over about a second, so the
    // game-over panel lands on a corpse rather than a paused game.
    if (!sim.alive && this.deathAt > 0) {
      const t = clamp01((time - this.deathAt) / 1.1)
      ctx.fillStyle = `rgba(24,2,8,${t * 0.32})`
      ctx.fillRect(0, 0, this.w, this.h)
    }

    if (input.showScanlines) {
      ctx.save()
      ctx.globalAlpha = 0.055
      ctx.fillStyle = '#000'
      for (let y = 0; y < this.h; y += 3) ctx.fillRect(0, y, this.w, 1)
      ctx.restore()
    }

    if (input.paused) {
      ctx.fillStyle = 'rgba(4,5,12,0.55)'
      ctx.fillRect(0, 0, this.w, this.h)
    }
  }

  // ── geometry ───────────────────────────────────────────────────────────

  /**
   * Interpolate the snake between the previous tick's body and this one, and
   * split the result wherever it teleports (a phase-wrap through a wall) so we
   * never draw a stripe across the whole arena.
   */
  private snakePolylines(input: RenderInput): Vec[][] {
    const { sim, prev, alpha } = input
    const cur = sim.snake
    const out: Vec[][] = []
    let run: Vec[] = []
    let last: Vec | null = null

    for (let i = 0; i < cur.length; i++) {
      const a = prev.length ? prev[Math.min(i, prev.length - 1)] : cur[i]
      const b = cur[i]
      // A body segment that "moved" more than a cell is a wrap, not a slide.
      const jumped = Math.abs(a.x - b.x) > 1.5 || Math.abs(a.y - b.y) > 1.5
      const p: Vec = jumped
        ? { x: b.x + 0.5, y: b.y + 0.5 }
        : { x: lerp(a.x, b.x, alpha) + 0.5, y: lerp(a.y, b.y, alpha) + 0.5 }

      if (last && (Math.abs(last.x - p.x) > 1.6 || Math.abs(last.y - p.y) > 1.6)) {
        if (run.length > 1) out.push(run)
        run = []
      }
      run.push(p)
      last = p
    }
    if (run.length) out.push(run)
    if (!out.length) out.push([{ x: cur[0].x + 0.5, y: cur[0].y + 0.5 }])
    return out
  }

  private interpolatedHead(input: RenderInput): Vec {
    const pts = this.snakePolylines(input)
    return pts[0][0]
  }

  /** Cell coordinates → canvas pixels, for HUD bits anchored to the board. */
  cellToScreen(x: number, y: number): { x: number; y: number } {
    return { x: this.originX + (x + 0.5) * this.cell, y: this.originY + (y + 0.5) * this.cell }
  }
}
